<template>
  <registration-layout
    :hide-nav="isLoading || hasTokenErrorMessage"
    :steps="steps"
    :step-id="currentStepID" 
    :size="currentStepLayoutSize"
    :revisit-name="revisitName"
    class="account-registration-complete"
    @clicked-step-id="(id) => handleStepClick(id)"
  >
    <loading-placeholder v-if="isLoading" :lines="10" />
    <template v-else-if="hasTokenErrorMessage">
      <h3>This link is no longer valid</h3>
      <p>
        {{ tokenErrorMessage }}
      </p>
      <a :href="RouteAssistant.getSignupUrl()" class="just-a-link">
        Sign up again
      </a>
    </template>
    <template v-else>
      <h3>{{ currentStepInfo.title }}</h3>
      <p v-if="currentStepInfo.subtitle">{{ currentStepInfo.subtitle }}</p>
      <!-- ABOUT -->
      <el-form
        v-if="!completeAccountData.hasUserRegistered"
        ref="step1Form"
        :class="{'hidden' : currentStepID !== 1}"
        :model="formData"
        :rules="step1FormRules"
        label-position="top"
      >
        <el-form-item label="Full Name" prop="name">
          <el-input ref="nameRef" v-model="formData.name" placeholder="Enter your full name" maxlength="100" />
        </el-form-item>
        <template v-if="!completeAccountData.viaGoogleAuth">
          <el-form-item label="Password" class="short-bottom" prop="password">
            <el-input v-model="formData.password" type="password" placeholder="Enter a password (minimum 8 chars)" maxlength="100" />
          </el-form-item>
          <el-form-item prop="passwordConfirm">
            <el-input v-model="formData.passwordConfirm" type="password" placeholder="Re-enter Password" maxlength="100" />
          </el-form-item>
        </template>
        <el-form-item label="Time Zone" prop="timezone">
          <single-select v-model="formData.timezone" placeholder="Choose a time zone...">
            <el-option v-for="tz in completeAccountData.listTimeZones" :key="tz.name" :value="tz.name" :label="tz.value" />
          </single-select>
        </el-form-item>

        <el-form-item>
          <dd-checkbox v-model="formData.acceptTerms" dd-size="medium">
            I agree to the <a href="https://www.donedone.com/terms-of-use" class="just-a-link" target="_blank">Terms of Use</a> *
          </dd-checkbox>
          <dd-checkbox v-model="formData.acceptEmailList" dd-size="medium">
            Subscribe to news and updates
          </dd-checkbox>
        </el-form-item>

        <dd-button 
          :is-disabled="disableStep1Continue"
          color="blue"
          class="marker--account-registration--about-you--continue" 
          @click="continueToStep2">Continue</dd-button>
      </el-form>
      <!-- COMPANY -->
      <el-form
        ref="step2Form"
        :class="{'hidden' : currentStepID !== 2}"
        :model="formData"
        :rules="step2FormRules"
        label-position="top"
      >
        <el-form-item label="Company name" prop="companyName">
          <el-input ref="companyRef" v-model="formData.companyName" placeholder="Enter your company's name" maxlength="100" />
        </el-form-item>
        <el-form-item label="Size" prop="companySize">
          <single-select v-model="formData.companySize" placeholder="Choose a size...">
            <el-option v-for="cs in completeAccountData.listCompanySizes" :key="cs.name" :value="cs.value" :label="cs.name" />
          </single-select>
        </el-form-item>
        <el-form-item label="Industry" prop="companyIndustry">
          <single-select v-model="formData.companyIndustry" placeholder="Choose an industry..." @change="toggleOtherIndustry">
            <el-option v-for="ci in completeAccountData.listCompanyIndustries" :key="ci.name" :value="ci.value" :label="ci.name" />
          </single-select>
        </el-form-item>
        <el-form-item v-if="hasOtherIndustry" prop="otherIndustry">
          <el-input ref="otherIndustryRef" v-model="otherIndustry" placeholder="Enter your industry" maxlength="100" />
        </el-form-item>
        <dd-button 
          :is-disabled="disableStep2Continue"
          :is-loading="isSubmitting"
          class="marker--account-registration--company--continue" 
          color="blue" @click="submitForm()">Continue</dd-button>
      </el-form>
    </template>
  </registration-layout>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'
import RegistrationLayout from '@/layouts/registration-layout.vue'
import MainContainer from '@/layouts/main-container.vue'

/* Mixins */

/* Components */

/* Helpers */
import RouteAssistant from '@/helpers/route-assistant'
import TimeAssistant from '@/helpers/time-assistant'
import TextAssistant from '@/helpers/text-assistant'

/* Data types */
import {
  IItemForSelection,
  IRegistrationOutputCompleteAccount,
} from '@/api/types/response.types'
import { IRegistrationInputCreateAccount } from '@/api/types/request.types'

interface IGoalInfo {
  type: string
  title: string
  subtitle: string
  selected: boolean
}

/* API */
import RegistrationAPI from '@/api/modules/registration.api'
import { EEntities } from '@/types'

export default defineComponent({
  name: 'AccountRegistrationComplete',

  components: {
    RegistrationLayout,
    MainContainer,
  },

  data() {
    return {
      EEntities,
      TextAssistant,
      tokenErrorMessage: '',
      isLoading: true,
      isSubmitting: false,
      hasOtherIndustry: false,
      otherIndustry: '',
      completeAccountData: {} as IRegistrationOutputCompleteAccount,
      RouteAssistant,
      currentStepID: 1,
      steps: [] as IItemForSelection[],
      stepInfos: [
        {
          id: 1,
          title: 'About You',
          layoutSize: 'tiny',
        },
        {
          id: 2,
          title: 'About Your Company',
          layoutSize: 'tiny',
        },
      ],
      goalInfos: [] as IGoalInfo[],
      formData: {
        name: '',
        companyName: '',
        companySize: '',
        companyIndustry: '',
        emailAddress: '',
        verificationToken: '',
        password: '',
        passwordConfirm: '',
        timezone: '',
        viaGoogleAuth: false,
        acceptTerms: false,
        acceptEmailList: false,
        listGoals: [] as string[],
        otherGoal: '',
        hubspotutk: '',
      } as IRegistrationInputCreateAccount,
      step1FormRules: {
        name: [
          {
            validator: (rule: any, value: any, callback: any) => {
              if (value && value.trim() !== '') {
                callback()
              } else {
                callback(new Error('Name is required'))
              }
            },
            trigger: 'blur',
          },
        ],
        password: [
          {
            validator: (rule: any, value: any, callback: any) => {
              // @ts-ignore
              if (this.completeAccountData.viaGoogleAuth) {
                callback()
              }

              if (value.length < 8) {
                callback(new Error('Password must be at least 8 characters'))
              } else if (!/\d/.test(value)) {
                callback(new Error('Password must contain a digit (0-9)'))
              } else {
                callback()
              }
            },
            trigger: 'blur',
          },
        ],
        passwordConfirm: [
          {
            validator: (rule: any, value: any, callback: any) => {
              // @ts-ignore
              if (this.completeAccountData.viaGoogleAuth) {
                callback()
              }
              // @ts-ignore
              if (value !== this.formData.password) {
                callback(new Error('Password must match'))
              } else {
                callback()
              }
            },
            trigger: 'blur',
          },
        ],
        timezone: [{ required: true, message: 'Timezone is required' }],
      },
      step2FormRules: {
        companyName: [
          {
            validator: (rule: any, value: any, callback: any) => {
              if (value && value.trim() !== '') {
                callback()
              } else {
                callback(new Error('Company name is required'))
              }
            },
          },
        ],
        companySize: [{ required: true, message: 'Size is required' }],
        companyIndustry: [{ required: true, message: 'Industry is required' }],
        otherIndustry: [
          {
            validator: (rule: any, value: any, callback: any) => {
              // @ts-ignore
              if (this.hasOtherIndustry && this.otherIndustry === '') {
                callback(new Error('Industry is required'))
              } else {
                callback()
              }
            },
          },
        ],
      },
    }
  },

  head(): any {
    return {
      title: 'Complete account registration',
    }
  },

  computed: {
    currentStepInfo(): any {
      return this.stepInfos.find(s => s.id === this.currentStepID)
    },
    currentStepLayoutSize(): string {
      return this.currentStepInfo.layoutSize
    },
    revisitName(): string {
      if (this.completeAccountData.hasUserRegistered) {
        return this.completeAccountData.name
      }

      return null
    },
    hasTokenErrorMessage(): boolean {
      return (
        this.tokenErrorMessage !== undefined &&
        this.tokenErrorMessage.length > 0
      )
    },
    disableStep1Continue(): boolean {
      return !this.formData.acceptTerms
    },
    disableStep2Continue(): boolean {
      return false
    },
  },

  async created() {
    this.isLoading = true
    await this.getCompleteAccountData()
    this.isLoading = false
    this.$nextTick(() => {
      if (this.completeAccountData.hasUserRegistered) {
        // @ts-ignore
        this.$refs.companyRef.focus()
      } else {
        // @ts-ignore
        this.$refs.nameRef.focus()
      }
    })
  },

  methods: {
    continueToStep2() {
      // @ts-ignore
      this.$refs.step1Form.validate(valid => {
        if (valid) {
          this.currentStepID = 2
          this.$nextTick(() => {
            // @ts-ignore
            this.$refs.companyRef.focus()
          })
        }
      })
    },
    toggleOtherIndustry() {
      this.hasOtherIndustry =
        this.formData.companyIndustry.toLowerCase().trim() === 'other'

      if (this.hasOtherIndustry) {
        this.$nextTick(() => {
          // @ts-ignore
          this.$refs.otherIndustryRef.focus()
        })
      }
    },
    async submitForm() {
      this.isSubmitting = true
      try {
        // Replace industry with typed in option...
        if (this.hasOtherIndustry) {
          this.formData.companyIndustry = this.otherIndustry
        }

        const response = await RegistrationAPI.post_CreateAccount(this.formData)
        window.location.href = RouteAssistant.getRegistrationAccountAddMembersUrl(
          response.id,
          response.email,
        )
      } catch (error) {
        this.$error.emit(error, true)
        this.isSubmitting = false
      }
    },
    handleStepClick(id: number) {
      // Only move step if it's behind us...
      if (this.currentStepID > id) {
        this.currentStepID = id
      }
    },
    async getCompleteAccountData() {
      try {
        this.completeAccountData = await RegistrationAPI.get_CompleteAccount(
          TextAssistant.urlTokenCleaner(this.$route.query.donedone_token as string),
          TextAssistant.urlTokenCleaner(this.$route.query.gauth_token as string),
          TextAssistant.urlTokenCleaner(this.$route.query.microsoft_token as string),
        )

        this.formData.name = this.completeAccountData.name
        this.formData.verificationToken = this.completeAccountData.verificationToken
        this.formData.emailAddress = this.completeAccountData.emailAddress
        this.formData.viaGoogleAuth = this.completeAccountData.viaGoogleAuth
        this.formData.timezone = TimeAssistant.guessTimezoneID()

        try {
          const hubspotCookie = document.cookie
            .split(';')
            .map(c => c.trim())
            .find(c => c.startsWith('hubspotutk='))
          const hubspotutk = hubspotCookie
            ? hubspotCookie.substring('hubspotutk='.length)
            : ''
          if (hubspotutk) {
            this.formData.hubspotutk = hubspotutk
          }
        } catch (error) {
          console.error('Error retrieving hubspotutk cookie:', error)
        }

        this.completeAccountData.listTaskGoals.forEach(t => {
          this.goalInfos.push({
            type: TextAssistant.itemSingularName(
              EEntities.PROJECTS,
            ).toLowerCase(),
            title: t.name,
            subtitle: t.description,
            selected: false,
          })
        })

        this.completeAccountData.listConversationGoals.forEach(c => {
          this.goalInfos.push({
            type: TextAssistant.itemSingularName(
              EEntities.MAILBOXES,
            ).toLowerCase(),
            title: c.name,
            subtitle: c.description,
            selected: false,
          })
        })

        if (this.completeAccountData.hasUserRegistered) {
          this.steps = [{ id: 2, name: 'Company' }, { id: 3, name: 'Team' }]
          this.currentStepID = 2
        } else {
          this.steps = [
            { id: 1, name: 'You' },
            { id: 2, name: 'Company' },
            { id: 3, name: 'Team' },
          ]
        }
      } catch (error) {
        this.tokenErrorMessage = error.data.message
      }
    },
  },
})
</script>

<style lang="scss">
.account-registration-complete {
  .just-a-link {
    margin-top: 20px;
  }

  h3 {
    margin-bottom: 4px;
  }

  .hidden {
    display: none;
  }

  .el-form-item {
    &.short-bottom {
      margin-bottom: 12px;
    }
  }

  .el-checkbox {
    margin-bottom: 0;
    font-weight: normal;
  }

  &__goals {
    width: 100%;
    padding-top: 20px;

    > :last-child {
      margin-top: 20px;
    }

    > div {
      display: flex;
      border: $border--solid-light;
      border-radius: 8px;
      margin-bottom: 10px;
      cursor: pointer;

      &:hover {
        transition: 0.3s;
        box-shadow: $--select-dropdown-shadow;
      }

      &.selected {
        border-color: $color--orange;
        color: $color--orange;

        &.task-goal {
          border-color: $projects--theme-color;
          color: $projects--theme-color;
        }
        &.conversation-goal {
          border-color: $mailboxes--theme-color;
          color: $mailboxes--theme-color;
        }
      }

      .icon-holder {
        padding: 10px 20px;
        display: flex;
        align-items: center;
        background-color: var(--color-background-primary);
        border-top-left-radius: inherit;
        border-bottom-left-radius: inherit;

        .ddi {
          font-size: 24px;
        }

        .ddi-task {
          color: $color--blue;
        }

        .ddi-conversation {
          color: $mailboxes--theme-color;
        }

        .ddi-star {
          color: $color--gray-45;
        }
      }

      .description {
        padding: 10px 20px;
      }

      .selected-holder {
        margin-left: auto;
        display: flex;
        align-items: center;
        padding-right: 10px;
      }
    }
  }
}
</style>
